import { addYears } from 'date-fns';
import { useTranslation } from 'next-i18next';
import DatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetCausesList } from '@/modules/periodicTasks/apihooks';
import { PeriodicOperationCategory } from '@/modules/periodicTasks/components/Details/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { usePropertySettings } from '@/modules/settings/property/components/logic';
import { useGetCompanyTeam } from '@/modules/settings/Team/apiHooks';
import AddEditTeamMember from '@/modules/settings/Team/components/AddEditTeamMember';
import { ICompanyTeamsSettings } from '@/modules/settings/Team/types';
import {
  ErrorMessage,
  InputField,
  InputLabel,
  SelectField,
} from '@/shared/components/atoms';
import { permissions, roles } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

const OperationDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { user } = useAuth();
  const { openModal, closeModal } = useModal();
  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<IPeriodicTasks.IOperationFormSchema>();
  const isSpraying =
    watch('operationType.id') === PeriodicOperationCategory.Spraying;

  const {
    data: usersList,
    isLoading: isUsersListLoading,
    refetch: refetchCompanyTeam,
  } = useGetCompanyTeam({
    companyId: user?.companyId,
    pageNumber: 1,
    // TODO: check with backend
    pageSize: 1000,
    roles: `${roles.SUPERVISOR}, ${roles.COMPANY_ADMIN}, ${roles.PRODUCTION_MANAGER}, ${roles.ONBOARDING_SUPERVISOR}`,
  });

  const { useGetCountryID } = usePropertySettings();
  const countryId = useGetCountryID(
    isSpraying ? selectedAsset?.country : undefined
  );
  const { data: causes } = useGetCausesList({
    countryId,
    paginated: false,
  });
  const selectedCausesIds = new Set(watch('causes')?.map((cause) => cause.id));

  const getAssigneeName = (obj: ICompanyTeamsSettings.ICompanyUser) =>
    `${obj.firstName} ${obj.lastName} ${
      obj?.id === user?.id ? `(${t('you')})` : ''
    }`;

  const handleTeamMemberAddedSuccess = () => {
    closeModal();
    toast.success(t(`warehouse-team-member-added`));
    refetchCompanyTeam();
  };

  const hasPermissionToAddTeamMember = UserHasPermission({
    action: permissions.CAN_ADD_TEAM_MEMBER_TO_WAREHOUSE,
    role: user?.userGroupId,
  });

  return (
    <div className='flex flex-col gap-16'>
      <InputField
        id='name'
        {...register('name', {
          setValueAs: (value) => (value?.length ? value : undefined),
        })}
        label={t('descriptive-name')}
        placeholder={t('descriptive-name')}
        className='w-full !rounded-xl bg-background-transparent desktop:max-h-56'
        labelClasseName='!text-14'
        borderColor='border-outline-variant'
        textColor='text-text-default'
        width='w-full'
        error={!!errors.name}
        errorMessage={errors.name?.message}
        py='!py-14'
      />
      <SelectField
        id='assignee'
        errorMessageId='assignee.id'
        getLabel={getAssigneeName}
        getValue={(obj: ICompanyTeamsSettings.ICompanyUser) => ({
          name: getAssigneeName(obj),
          id: obj.id,
        })}
        value={usersList?.users.filter(
          (user) => user.id === watch('assignee')?.id
        )}
        isDisabled={isUsersListLoading}
        borderColor={themeColors().outline.variant}
        label={t('assignee')}
        placeholder={t('assignee')}
        list={usersList?.users ?? []}
        createOption={
          hasPermissionToAddTeamMember
            ? {
                title: t('add-team-member-to-warehouse'),
                subTitle: t('add-team-member-to-operation-sub-title'),
                onClick: () => {
                  openModal(
                    <AddEditTeamMember
                      onSuccess={handleTeamMemberAddedSuccess}
                      formName='create periodic operation - add team member from select field'
                    />,
                    {
                      variant: 'full',
                      title: t('add-team-member-to-warehouse'),
                    }
                  );
                },
              }
            : undefined
        }
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
      />
      <div className='flex w-full flex-col'>
        <InputLabel
          label={t('planned-date')}
          id='plannedDate'
          className='capitalize'
        />
        <Controller
          name='plannedDate'
          control={control}
          render={({ field: { onChange } }) => (
            <DatePicker
              wrapperClassName='relative'
              locale={language}
              selected={watch('plannedDate')}
              className='w-full cursor-pointer rounded-xl border-xs border-outline-variant bg-background-transparent px-12 py-14 capitalize'
              placeholderText={t('planned-date')}
              onChange={(date) => {
                if (date) onChange(date);
              }}
              maxDate={addYears(new Date(), 1)}
              minDate={new Date()}
            />
          )}
        />
        {errors?.plannedDate && (
          <ErrorMessage errorMessage={errors.plannedDate.message ?? ''} />
        )}
      </div>
      {isSpraying && (
        <SelectField
          id='causes'
          isMulti
          getLabel={(obj: IGeneral.NameIdPair) => obj.name}
          getValue={(obj: IGeneral.NameIdPair) => obj}
          value={causes?.items.filter((cause) =>
            selectedCausesIds?.has(cause.id)
          )}
          borderColor={themeColors().outline.variant}
          label={t('cause')}
          placeholder={t('cause')}
          list={causes?.items}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full min-w-0'
        />
      )}
      <InputField
        id='notes'
        {...register('notes', {
          setValueAs: (value) => (value?.length ? value : undefined),
        })}
        multiline
        label={`${t('notes')} (${t('optional')})`}
        error={!!errors?.notes}
        errorMessage={errors.notes?.message}
        placeholder={`${t('notes')} (${t('optional')})`}
        className='w-full !rounded-xl bg-background-transparent desktop:max-h-56'
        labelClasseName='!text-14'
        borderColor='border-outline-variant'
        textColor='text-text-default'
        width='w-full'
        py='!py-14'
      />
    </div>
  );
};

export default OperationDetails;
